import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/atoms/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { MAP_LAYER_PANEL_WIDTH_CLASS } from '@/features/map/components/mapBarGeometry';
import {
  useAllWorkspacesQuery,
  useWorkspaceQuery,
} from '@/features/workspaces/services/workspace.api';
import { filterWorkspacesByName } from '@/features/workspaces/utils/filterWorkspaces';
import { cn } from '@/libs/utils/cn';
import type { IWorkspace } from '@/types/api';
import { WorkspaceRowMenu } from './WorkspaceRowMenu';
import { THIN_SCROLLBAR } from '@/libs/utils/thinScrollbar';

const ITEM_CLASS =
  'flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none ' +
  'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent ' +
  'focus-visible:text-accent-foreground';

interface WorkspaceSwitcherProps {
  activeId: string | null;
  onNew: () => void;
  onEdit: (workspace: IWorkspace) => void;
  onDelete: (workspace: IWorkspace) => void;
  onSelect: (id: string) => void;
}

/**
 * Workspace switcher — the single surface for choosing and managing workspaces.
 * The dropdown drops from the navbar trigger and holds a searchable, scrollable
 * list where the active workspace is pinned on top; each row switches on click
 * and carries a kebab for Edit / Delete. There is no separate "manage" sheet.
 *
 * Accessibility: Radix owns the disclosure. The list stays a `role="menu"` of
 * `role="menuitem"` buttons with arrow-key roving; the filter Input and the
 * per-row kebab are intentionally outside the roving set, and Escape inside the
 * filter only exits the field.
 */
export function WorkspaceSwitcher({
  activeId,
  onNew,
  onEdit,
  onDelete,
  onSelect,
}: WorkspaceSwitcherProps) {
  const { data: workspace, isLoading: isActiveLoading } = useWorkspaceQuery(activeId);
  const { data: listData } = useAllWorkspacesQuery();

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  // At most one row's kebab menu is open at a time.
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const label = workspace?.name ?? (activeId && isActiveLoading ? '…' : 'Choose a workspace');

  const allWorkspaces = listData?.workspaces ?? [];
  // Active workspace pinned to the top of the (filtered) list.
  const rows = useMemo(() => {
    const matches = filterWorkspacesByName(allWorkspaces, filter);
    return matches.slice().sort((a, b) => Number(b.id === activeId) - Number(a.id === activeId));
  }, [allWorkspaces, filter, activeId]);

  useEffect(() => {
    if (!open) {
      setFilter('');
      setOpenRowId(null);
    }
  }, [open]);

  function closeAnd(action?: () => void) {
    setOpen(false);
    action?.();
  }

  // Arrow-key roving across the row buttons (the kebab and filter stay out of it).
  function onMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;

    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const next = (current + delta + items.length) % items.length;
    items[next]?.focus();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Text, like the nav items beside it: a muted "Workspace" eyebrow, the name, a chevron. */}
        <button
          type="button"
          aria-label={`Active workspace: ${label}`}
          aria-haspopup="menu"
          className="group flex max-w-full items-center gap-1.5 rounded-sm py-1 text-sm outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="shrink-0 text-muted-foreground">Workspace</span>
          <span
            className={cn(
              'min-w-0 flex-1 truncate font-semibold text-primary-700 group-hover:text-primary',
              !workspace?.name && 'font-medium text-muted-foreground',
            )}
          >
            {label}
          </span>
          <ChevronDown
            className={cn(
              'size-3.5 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className={cn(MAP_LAYER_PANEL_WIDTH_CLASS, 'rounded-[10px] border-border p-2 shadow-modal')}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }}
        onEscapeKeyDown={(event) => {
          if (event.target instanceof HTMLInputElement) event.preventDefault();
        }}
      >
        <div
          ref={menuRef}
          role="menu"
          aria-label="Workspaces"
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
          className="flex flex-col gap-1"
        >
          {allWorkspaces.length > 0 && (
            <p className="px-2 pt-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Switch workspace
            </p>
          )}

          {allWorkspaces.length > 1 && (
            // Centered filter, keeping the row-matched width.
            <div className="px-5 py-2">
              <Input
                type="search"
                className="h-8 min-h-0 text-sm"
                aria-label="Filter workspaces"
                placeholder="Filter workspaces…"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                startIcon={<Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
              />
            </div>
          )}

          {/* Only the rows scroll (capped ~10); search + New stay pinned. */}
          <div className={`flex max-h-80 flex-col gap-0.5 overflow-y-auto ${THIN_SCROLLBAR}`}>
            {rows.length === 0 && (
              <p role="status" className="px-2 py-1.5 text-sm text-muted-foreground">
                {allWorkspaces.length ? 'No matches' : 'No workspaces yet'}
              </p>
            )}

            {rows.map((ws) => {
              const isActive = ws.id === activeId;
              return (
                <div key={ws.id} className="flex items-center gap-0.5">
                  <button
                    type="button"
                    role="menuitem"
                    data-active={isActive ? 'true' : undefined}
                    onClick={() => closeAnd(isActive ? undefined : () => onSelect(ws.id))}
                    className={cn(ITEM_CLASS, isActive && 'bg-muted')}
                  >
                    <Check
                      className={cn('h-4 w-4 shrink-0 text-primary', !isActive && 'invisible')}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate">{ws.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {ws.location_count} loc.
                    </span>
                  </button>
                  <WorkspaceRowMenu
                    workspaceName={ws.name}
                    open={openRowId === ws.id}
                    onOpenChange={(next) => setOpenRowId(next ? ws.id : null)}
                    onRename={() => closeAnd(() => onEdit(ws))}
                    onDelete={() => closeAnd(() => onDelete(ws))}
                  />
                </div>
              );
            })}
          </div>

          <div className="h-px bg-border" role="separator" />

          <button
            type="button"
            role="menuitem"
            onClick={() => closeAnd(onNew)}
            className={cn(
              ITEM_CLASS,
              'font-medium text-link hover:text-link focus-visible:text-link',
            )}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
            New workspace
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
